// Файл storage.js

/**
 * Модуль предоставляет безопасную оболочку над локальным и сессионным хранилищем браузера.
 * Он централизует обработку недоступного storage и сериализацию значений.
 * Компоненты используют оболочку вместо прямых обращений к глобальным объектам.
 */

import { get_interface_customization_settings } from '../shared_code/interface_customization/interface_customization_store';



const storage_version = 2;



const is_tab_click_key = (key) => String(key || '').endsWith('_click');
const is_tab_state_key = (key) => String(key || '').startsWith('tab_');

const apply_tab_storage_policy = (key, payload) => {
    const settings = get_interface_customization_settings();

    if (is_tab_click_key(key) && !settings.restore_open_tabs) {
        return undefined;
    }

    if (!is_tab_state_key(key) || !payload || typeof payload !== 'object') {
        return payload;
    }

    const next_payload = { ...payload };

    if (!settings.remember_last_tab) {
        next_payload.activeTab = 0;
        next_payload.activeTabKey = null;
        next_payload.tabHistory = [];
    }

    if (!settings.restore_open_tabs) {
        next_payload.openTabs = [];
    }

    return next_payload;
};

const storage_migrations = {
    1: (payload) => payload,
};

const create_storage_envelope = (state) => ({
    storage_version,
    saved_at: new Date().toISOString(),
    payload: state,
});

const is_storage_envelope = (value) => (
    value
    && typeof value === 'object'
    && value.storage_version !== undefined
    && Object.prototype.hasOwnProperty.call(value, 'payload')
);

const get_local_storage = () => {
    if (
        typeof window === 'undefined'
        || !window.localStorage
    ) {
        return null;
    }

    return window.localStorage;
};

const migrate_storage_value = (value) => {
    if (!is_storage_envelope(value)) {
        return {
            payload: value,
            should_resave: true,
        };
    }

    const source_version = Number(value.storage_version);

    if (!Number.isInteger(source_version)) {
        return {
            payload: undefined,
            should_resave: false,
        };
    }

    if (source_version > storage_version) {
        return {
            payload: undefined,
            should_resave: false,
        };
    }

    let current_version = source_version;
    let payload = value.payload;

    while (current_version < storage_version) {
        const migrate = storage_migrations[current_version];

        if (typeof migrate !== 'function') {
            return {
                payload: undefined,
                should_resave: false,
            };
        }

        payload = migrate(payload);
        current_version += 1;
    }

    return {
        payload,
        should_resave: current_version !== source_version,
    };
};

const parse_stored_value = (serialized_state) => {
    try {
        return JSON.parse(serialized_state);

    } catch (error) {
        return serialized_state;
    }
};

export const storage = {
    save_state: (key, state) => {
        try {
            const local_storage = get_local_storage();
            const stored_state = apply_tab_storage_policy(key, state);

            if (!local_storage) {
                return false;
            }

            if (stored_state === undefined) {
                local_storage.removeItem(key);
                return true;
            }

            local_storage.setItem(
                key,
                JSON.stringify(create_storage_envelope(stored_state))
            );

            return true;

        } catch (error) {
            console.warn('Storage save error:', error);

            return false;
        }
    },

    loadState: (key) => {
        try {
            const local_storage = get_local_storage();

            if (!local_storage) {
                return undefined;
            }

            const serialized_state = local_storage.getItem(key);

            if (serialized_state === null) {
                return undefined;
            }

            const parsed_state = parse_stored_value(serialized_state);
            const migration_result = migrate_storage_value(parsed_state);

            if (migration_result.payload === undefined) {
                return undefined;
            }

            const policy_payload = apply_tab_storage_policy(
                key,
                migration_result.payload
            );

            if (migration_result.should_resave && policy_payload !== undefined) {
                storage.save_state(key, policy_payload);
            }

            return policy_payload;

        } catch (error) {
            console.warn('Storage load error:', error);

            return undefined;
        }
    },

    removeState: (key) => {
        try {
            const local_storage = get_local_storage();

            if (!local_storage) {
                return false;
            }

            local_storage.removeItem(key);

            return true;

        } catch (error) {
            console.warn('Storage remove error:', error);

            return false;
        }
    },

    get_item: (key) => {
        const value = storage.loadState(key);

        if (value === undefined) {
            return null;
        }

        if (typeof value === 'string') {
            return value;
        }

        return JSON.stringify(value);
    },

    set_item: (key, value) => {
        let parsed_value = value;

        if (typeof value === 'string') {
            try {
                parsed_value = JSON.parse(value);

            } catch (error) {
                parsed_value = value;
            }
        }

        return storage.save_state(key, parsed_value);
    },

    all_keys: () => {
        try {
            const local_storage = get_local_storage();

            return local_storage ? Object.keys(local_storage) : [];

        } catch (error) {
            console.warn('Storage keys error:', error);

            return [];
        }
    },

    keys: () => {
        const keys = storage.all_keys();
        const settings = get_interface_customization_settings();

        if (!settings.restore_open_tabs) {
            return keys.filter((key) => !is_tab_click_key(key));
        }

        return keys;
    },

    remove_by_prefix: (prefix) => {
        storage.keys()
            .filter((key) => String(key).startsWith(String(prefix)))
            .forEach((key) => storage.removeState(key));
    },
};
